import { useEffect, useRef, useState, type ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Search, HardDrive, Users, Cpu, Settings, ShieldCheck, Plus, Server as ServerIco, CornerDownLeft, BarChart3, PiggyBank,
  Construction, Network, Gauge, MessageSquare, Trash2, Folder, Power, Activity, LayoutDashboard, Terminal, ScrollText,
  Package, Boxes, Zap, Map as MapIcon, Megaphone, Save, Gamepad2, Play, RefreshCw, AlertTriangle, User as UserIcon,
  SlidersHorizontal, LogIn,
} from "lucide-react";
import { serverStore } from "../../stores/server.store";
import { userStore } from "../../stores/user.store";
import { cn } from "../../utils/cn";
import { buildPaletteItems, completeQuery, searchPalette, type PaletteIcon, type PaletteItem } from "../../utils/palette-search";
import { scrollToSection } from "../../utils/scroll-to-section";
import { can as canUser } from '@/utils/permissions';

const ICONS: Record<PaletteIcon, ReactNode> = {
  plus: <Plus size={16} />, server: <ServerIco size={16} />, stats: <BarChart3 size={16} />, storage: <HardDrive size={16} />,
  funding: <PiggyBank size={16} />, users: <Users size={16} />, audit: <ShieldCheck size={16} />, system: <Cpu size={16} />,
  settings: <Settings size={16} />, maintenance: <Construction size={16} />, network: <Network size={16} />, ram: <Gauge size={16} />,
  discord: <MessageSquare size={16} />, cleanup: <Trash2 size={16} />, files: <Folder size={16} />, power: <Power size={16} />,
  monitor: <Activity size={16} />, overview: <LayoutDashboard size={16} />, console: <Terminal size={16} />, logs: <ScrollText size={16} />,
  software: <Package size={16} />, modpacks: <Boxes size={16} />, optimize: <Zap size={16} />, players: <Users size={16} />,
  map: <MapIcon size={16} />, announcements: <Megaphone size={16} />, backups: <Save size={16} />, general: <ServerIco size={16} />,
  game: <Gamepad2 size={16} />, java: <Play size={16} />, updates: <RefreshCw size={16} />, danger: <AlertTriangle size={16} />,
  account: <UserIcon size={16} />, ui: <SlidersHorizontal size={16} />, logins: <LogIn size={16} />,
};

/**
 * Strg+K: Server, Seiten und Unterpunkte suchen. „wartung" springt zu System › Wartungsmodus,
 * „ATM10 ram" direkt zum Arbeitsspeicher von ATM 10; Tab ergänzt wie bei einer Suchmaschine
 * (Suchlogik: utils/palette-search.ts).
 */
export const CommandPalette = observer(() => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { currentUser } = userStore;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
      else if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // „Suchen …"-Knopf in der Seitenleiste (Rail) öffnet die Palette ebenfalls.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("gm:open-palette", onOpen);
    return () => window.removeEventListener("gm:open-palette", onOpen);
  }, []);

  useEffect(() => {
    if (open) { setQ(""); setIdx(0); setTimeout(() => inputRef.current?.focus(), 20); }
  }, [open]);

  // Mit den Pfeiltasten ausgewählten Eintrag im Blick behalten.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  if (!currentUser || !open) return null;

  const can = (p: string) => canUser(currentUser, p);
  const items = buildPaletteItems(
    serverStore.getSortedServers().map((s) => ({ id: s.id, name: s.name, type: s.type, status: s.status, version: (s as any).version })),
    can,
  );
  // „Aktueller Server" nur, solange man wirklich in einem Server ist.
  const activeServerId = location.pathname.match(/^\/server\/([^/]+)/)?.[1] ?? null;
  const results = searchPalette(items, q, { activeServerId });
  const clampedIdx = Math.min(idx, Math.max(0, results.length - 1));

  // Tab-Vorschlag zum gewählten Eintrag; passt er zum Getippten, erscheint der Rest grau dahinter.
  const completion = completeQuery(q, results[clampedIdx], activeServerId);
  const ghost = completion && completion.toLowerCase().startsWith(q.toLowerCase()) ? completion.slice(q.length) : "";
  const accept = () => {
    if (!completion) return false;
    setQ(completion);
    setIdx(0);
    return true;
  };

  const run = (c?: PaletteItem) => {
    if (!c) return;
    setOpen(false);
    if (c.action === "create-server") { serverStore.showCreateModal = true; return; }
    if (c.action === "system-monitor") { serverStore.showSystemDetails = true; return; }
    if (c.serverId) serverStore.selectedServerId = c.serverId;
    if (c.to) navigate(c.to);
    if (c.anchor) scrollToSection(c.anchor);
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-ink/70 z-[9998] flex items-start justify-center pt-[15vh] px-4 animate-in fade-in duration-100"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="w-full max-w-lg bg-surface-2 border border-line rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-100">
        <div className="flex items-center gap-3 px-4 border-b border-line">
          <Search size={18} className="text-fg-muted shrink-0" />
          <div className="relative flex-1 min-w-0">
            {/* Grauer Vorschlag hinter dem Getippten (gleiche Schrift/Abstände wie das Feld) */}
            <div aria-hidden="true" className="absolute inset-0 py-3.5 text-sm whitespace-pre overflow-hidden pointer-events-none">
              <span className="invisible">{q}</span>
              <span className="text-fg-muted/50">{ghost}</span>
            </div>
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => { setQ(e.target.value); setIdx(0); }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setIdx(Math.min(results.length - 1, clampedIdx + 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setIdx(Math.max(0, clampedIdx - 1)); }
                else if (e.key === "Enter") { e.preventDefault(); run(results[clampedIdx]); }
                else if (e.key === "Tab") { e.preventDefault(); accept(); }
                else if (e.key === "ArrowRight" && ghost && e.currentTarget.selectionStart === q.length) { e.preventDefault(); accept(); }
              }}
              placeholder={'Suchen … z. B. „wartung“ oder „ATM10 ram“'}
              aria-label="Suchen"
              className="relative w-full bg-transparent py-3.5 text-sm text-fg placeholder-fg-muted/60 focus:outline-none"
            />
          </div>
          {completion && (
            <span className="hidden sm:flex items-center gap-1.5 shrink-0 max-w-[40%] text-[11px] text-fg-muted" title={`Tab ergänzt: ${completion}`}>
              <kbd className="font-mono border border-line rounded px-1.5 py-0.5">Tab</kbd>
              {!ghost && <span className="truncate">{completion}</span>}
            </span>
          )}
          <kbd className="text-[10px] font-mono text-fg-muted border border-line rounded px-1.5 py-0.5">esc</kbd>
        </div>
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1.5">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-fg-muted">
              Nichts gefunden.
              <div className="mt-1 text-xs">Tipp: Servername + Stichwort, z. B. „ATM10 backups“.</div>
            </div>
          ) : results.map((c, i) => (
            <button
              key={c.id}
              data-active={i === clampedIdx ? "true" : undefined}
              onMouseEnter={() => setIdx(i)}
              onClick={() => run(c)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-2 text-left transition-colors",
                i === clampedIdx ? "bg-ember/10" : "hover:bg-white/[0.04]"
              )}
            >
              <span className={cn("shrink-0", i === clampedIdx ? "text-ember" : "text-fg-muted")}>{ICONS[c.icon]}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-fg truncate">{c.label}</span>
                {c.sub && <span className="block text-[11px] text-fg-muted truncate">{c.sub}</span>}
              </span>
              {i === clampedIdx && <CornerDownLeft size={14} className="text-fg-muted shrink-0" />}
            </button>
          ))}
        </div>
        {!q.trim() && (
          <div className="px-4 py-2 border-t border-line text-[11px] text-fg-muted">
            Tipp: Auch Einstellungen findest du hier – „wartung“, „discord“ oder Servername + „ram“. Tab ergänzt.
          </div>
        )}
      </div>
    </div>,
    document.body
  );
});
